import type { ListBuilderConfiguration } from '../types';

/**
 * Client Location list configuration
 * Shows locations for a specific client
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_LOCATION_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-locations',
			cacheTTL: 600000,
			name: 'Client Locations',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'location-code',
					fields: [
						{
							id: 'code-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'location',
								idField: 'id',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'code', objectTable: 'location' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'location' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Code' }],
					minWidth: 150,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'code', objectTable: 'location' },
					],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'location-address',
					fields: [
						{
							id: 'address-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'locationInfo', objectTable: 'location' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Address' }],
					minWidth: 200,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'capacity',
					fields: [
						{
							id: 'capacity-field',
							dataConfig: {
								type: 'template',
								template: '{location.capacity} {location.units}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Capacity' }],
					minWidth: 100,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'capacity',
							objectTable: 'location',
						},
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'top',
				},
				{
					id: 'items-count',
					fields: [
						{
							id: 'items-count-field',
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'showItems',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemsCount', objectTable: 'location' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Items' }],
					minWidth: 80,
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'top',
				},
				{
					id: 'total-quantity',
					fields: [
						{
							id: 'totalQuantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'totalQuantity', objectTable: 'location' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-qty', text: 'Qty' }],
					minWidth: 80,
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'top',
				},
				{
					id: 'fill-percentage',
					fields: [
						{
							id: 'fillPercentage-field',
							dataConfig: {
								type: 'template',
								template: '{location.fillPercentage}%',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-fill', text: 'Fill %' }],
					minWidth: 80,
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 80 },
					verticalAlignment: 'top',
				},
				{
					id: 'stock-level',
					fields: [
						{
							id: 'stockLevel-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'stockLevel', objectTable: 'location' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								valueMapping: [
									{
										value: 'Empty',
										label: 'Empty',
										color: { type: 'neutral' },
									},
									{
										value: 'Low',
										label: 'Low',
										color: { type: 'neutral' },
									},
									{
										value: 'Medium',
										label: 'Medium',
										color: { type: 'warning' },
									},
									{
										value: 'Full',
										label: 'Full',
										color: { type: 'success' },
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-level', text: 'Level' }],
					minWidth: 90,
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 90 },
					verticalAlignment: 'top',
				},
				{
					id: 'status',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'statusFormatted',
									objectTable: 'location',
								},
								defaultValue: 'Unknown',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h5', text: 'Status' }],
					minWidth: 100,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'status', objectTable: 'location' },
					],
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			description: 'Locations for this client',
			defaultSortId: 'column-location-code-asc',
			requiredFields: [
				'location._id',
				'location.code',
				'location.description',
				'location.status',
				'location.capacity',
				'location.units',
				'location.clientAddressId',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['locationStatus'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/locations/:id',
			objectLabel: 'Location',
			objectTable: 'location',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'code', fieldLabel: 'Code' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'capacity', fieldLabel: 'Capacity' },
				{ fieldName: 'units', fieldLabel: 'Units' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'clientAddressId', fieldLabel: 'Client Address ID' },
			],
		},
	],
	tableSorts: [
		{
			id: 'code',
			name: 'Code',
			fields: [
				{ direction: 'ASC', fieldName: 'code', objectTable: 'location' },
			],
			description: 'Sort locations by code',
		},
		{
			id: 'capacity',
			name: 'By Capacity',
			fields: [
				{ direction: 'DESC', fieldName: 'capacity', objectTable: 'location' },
			],
			description: 'Sort by capacity (highest first)',
		},
		{
			id: 'status',
			name: 'By Status',
			fields: [
				{ direction: 'ASC', fieldName: 'status', objectTable: 'location' },
				{ direction: 'ASC', fieldName: 'code', objectTable: 'location' },
			],
			description: 'Sort by status then code',
		},
	],
};
